import cn from 'classnames';
import React, {
    useEffect,
    useRef,
    useState,
    useMemo,
} from 'react';

import {ReactComponent as ArrowDown} from 'shared/assets/arrow-down-filled.svg';
import {EEntityType} from 'shared/constants/entities';
import {useAppHistory} from 'shared/hooks/use-app-history';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';
import {
    generateRoute,
    selectIsMenuOpen,
    selectMenuSearchLine,
    selectSelectedMenusIds,
} from 'store/slices/menu-slice';
import {menuSliceActions} from 'store/slices/menu-slice/menu-slice';
import {IMenuStructureItem} from 'store/slices/menu-slice/menu-slice-types';

import {NewSideMenuList} from '../new-side-menu-list';

interface INewSideMenuListItemProps {
    item: IMenuStructureItem;
    path?: string;
    depth: number;
}

export const NewSideMenuListItem = ({
    item,
    path = '',
    depth,
}: INewSideMenuListItemProps) => {
    const dispatch = useAppDispatch();

    const history = useAppHistory();

    const [isOpen, setIsOpen] = useState(false);

    const searchLine = useAppSelector(selectMenuSearchLine);
    const isMenuOpen = useAppSelector(selectIsMenuOpen);
    const selectedMenusIds = useAppSelector(selectSelectedMenusIds);

    const {
        insertSelectedMenuItemAtIndex,
        setSelectedMenuItemsIds,
        setIsMenuOpen,
        setSearchLine,
    } = menuSliceActions;

    const hasChildren = useMemo(() => (
        !!item.children?.length && item.entityType !== EEntityType.TABS_WITH_REFERENCES
    ), [item]);

    const isSelected = useMemo(() => (
        history.location.pathname === generateRoute({
            ...item,
            skipRawChildrenCheck: true,
        })
    ), [item, history.location.pathname]);

    const itemRef = useRef<HTMLDivElement>(null);

    const handleItemClick = () => {
        if (hasChildren) {
            dispatch(insertSelectedMenuItemAtIndex({id: item.id, index: depth}));
        } else {
            if (!depth) { // на случай выбора корневых элементов без потомков
                setSelectedMenuItemsIds([item.id]);
            }
            history.push(generateRoute(item));
            dispatch(setSearchLine(''));
            dispatch(setIsMenuOpen(false));
        }
    };

    useEffect(() => {
        if (searchLine === '') {
            setIsOpen(selectedMenusIds.includes(item.id));
        } else if (!isOpen) setIsOpen(true);
    }, [searchLine]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout>;

        if (!isMenuOpen) {
            timeoutId = setTimeout(() => {
                setIsOpen(false);
            }, 200);
        } else if (selectedMenusIds.includes(item.id)) setIsOpen(true);

        return () => clearTimeout(timeoutId);
    }, [isMenuOpen]);

    useEffect(() => {
        if (isMenuOpen && isSelected && depth > 0) {
            itemRef.current?.scrollIntoView({block: 'center'});
        }
    }, [isMenuOpen]);

    useEffect(() => {
        if (isMenuOpen) {
            setIsOpen(selectedMenusIds.includes(item.id));
        }
    }, [selectedMenusIds, isMenuOpen]);

    return (
        <>
            <div
                ref={itemRef}
                className={cn(
                    'new-side-menu__list__item',
                    {
                        'new-side-menu__list__item_depth-1': depth === 1,
                        'new-side-menu__list__item_selected': isSelected,
                        'new-side-menu__list__item_child': depth > 0,
                    },
                    'no-select',
                )}
                onClick={() => {
                    handleItemClick();
                }}
            >
                <a
                    href={generateRoute(item)}
                    onClick={e => e.preventDefault()}
                >
                    <div
                        className={cn(
                            'new-side-menu__list__item__item-title',
                            {
                                'new-side-menu__list__item__item-title_selected':
                                    selectedMenusIds.includes(item.id),
                            },
                        )}
                        style={{paddingLeft: depth * 24}}
                    >
                        {item.itemTitle}
                    </div>
                </a>
                <div>
                    {hasChildren && (
                        <ArrowDown className={cn(
                            'new-side-menu__list__item__arrow',
                            {'new-side-menu__list__item__arrow_rotated': isOpen},
                        )}
                        />
                    )}
                </div>
            </div>
            {hasChildren && isOpen && (
                <div className={cn('new-side-menu__list__child')}>
                    <NewSideMenuList
                        items={item.children}
                        path={!depth ? '' : `${path}${item.entityName}/`}
                        depth={depth + 1}
                    />
                </div>
            )}
        </>
    );
};
